// src/pages/InvoicesListPage.js
import React, { useState, useEffect, useRef } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/styleUtils';
import styles from '../styles/InvoicesListPage.module.css';

const currencyILS = (n) => new Intl.NumberFormat('en-IL', { style: 'currency', currency: 'ILS' }).format(n || 0);

const InvoicesListPage = () => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const isMounted = useRef(true);

    const { userRole } = useAuth();
    const allowedRoles = ['Manager', 'Accounts Manager', 'Sales'];

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    useEffect(() => {
        if (!userRole) return;

        if (!allowedRoles.includes(userRole)) {
            setError("Access Denied.");
            setLoading(false);
            return;
        }

        const invoicesQuery = query(collection(db, 'invoices'), orderBy('createdAt', 'desc'));
        
        const unsubscribe = onSnapshot(invoicesQuery, (snapshot) => {
            if (!isMounted.current) return;
            const invoicesData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setInvoices(invoicesData);
            setLoading(false);
        }, (err) => {
            console.error("Error fetching invoices:", err);
            if (isMounted.current) {
                setError("Failed to load invoices. Please try again.");
                setLoading(false);
            }
        });

        return () => unsubscribe();
    }, [userRole]);

    const getStatusClass = (status) => {
        const statusKey = status?.toLowerCase().replace(/\s+/g, '') || 'default';
        return styles[`status-${statusKey}`] || styles.statusDefault;
    };

    if (loading) {
        return <div className={styles.loadingIndicator}>Loading Invoices...</div>;
    }

    if (error) {
        return <div className={styles.errorMessage}>{error}</div>;
    }

    return (
        <div className={styles.pageContainer}>
            <div className={styles.header}>
                <h1 className={styles.pageTitle}>Invoices</h1>
                <Link to="/invoices/new" className={styles.createButton}>Create New Invoice</Link>
            </div>

            <div className={styles.tableContainer}>
                <table className={styles.invoiceTable}>
                    <thead>
                        <tr>
                            <th>Invoice #</th>
                            <th>Client</th>
                            <th>Issue Date</th>
                            <th>Due Date</th>
                            <th>Total Amount</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {invoices.length === 0 ? (
                            <tr>
                                <td colSpan="7" className={styles.noInvoices}>No invoices found.</td>
                            </tr>
                        ) : (
                            invoices.map(invoice => (
                                <tr key={invoice.id}>
                                    <td>{invoice.invoiceNumber}</td>
                                    <td>{invoice.clientName}</td>
                                    <td>{formatDate(invoice.issueDate?.toDate())}</td>
                                    <td>{formatDate(invoice.dueDate?.toDate())}</td>
                                    <td>{currencyILS(invoice.totalAmount)}</td>
                                    <td>
                                        <span className={`${styles.statusBadge} ${getStatusClass(invoice.status)}`}>
                                            {invoice.status}
                                        </span>
                                    </td>
                                    <td>
                                        <Link to={`/invoices/${invoice.id}`} className={styles.viewButton}>View</Link>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default InvoicesListPage;
